/**
 * The three capabilities behind the passkey surfaces, each one answer per
 * page. `defer` holds every probe until its first subscriber, so importing
 * this where `PublicKeyCredential` does not exist costs and throws nothing;
 * `shareReplay(1)` hands every later subscriber the one answer already read.
 *
 * A throwing or rejecting probe answers `false`: the question each stream
 * answers is "can this be offered", and an unanswerable probe is a no. There
 * is no third state for a surface to render.
 */
import { type Observable, catchError, defer, of, shareReplay, switchMap } from 'rxjs';

/** Whether this browser can begin a WebAuthn ceremony at all — a sign-in may
 *  answer from a roaming key or a nearby phone, not only this device. */
export const webAuthnSupported$: Observable<boolean> = defer(() =>
  of(typeof PublicKeyCredential !== 'undefined'),
).pipe(shareReplay(1));

/** Whether this device can enrol the platform-bound, user-verifying passkeys
 *  the creation ceremony pins (`authenticatorAttachment: 'platform'`). */
export const passkeySupported$: Observable<boolean> = webAuthnSupported$.pipe(
  switchMap((supported) =>
    supported &&
    typeof PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function'
      ? defer(() => PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()).pipe(
          catchError(() => of(false)),
        )
      : of(false),
  ),
  shareReplay(1),
);

/** Whether a saved passkey can be offered inside a field's own autofill
 *  (`navigator.credentials.get({ mediation: 'conditional' })`). */
export const conditionalMediation$: Observable<boolean> = webAuthnSupported$.pipe(
  switchMap((supported) =>
    supported && typeof PublicKeyCredential.isConditionalMediationAvailable === 'function'
      ? defer(() => PublicKeyCredential.isConditionalMediationAvailable()).pipe(
          catchError(() => of(false)),
        )
      : of(false),
  ),
  shareReplay(1),
);
